import { useState } from 'react';

export type PlanType = 'vitrine' | 'ecommerce';
export type Engagement = 'sans' | '6mois' | '12mois';

export interface Plan {
  name: string;
  prices: Record<Engagement, number>;
  color: string;
  icon: string;
  popular: boolean;
  badge: string | null;
  description: string;
  features: string[];
  type: PlanType;
}

export const VITRINE_PLANS: Plan[] = [
  {
    name: 'Essentiel',
    prices: { sans: 69, '6mois': 59, '12mois': 49 },
    color: 'teal',
    icon: 'ri-layout-line',
    popular: false,
    badge: null,
    description: 'Présence en ligne simple et efficace',
    type: 'vitrine',
    features: [
      'Site vitrine (1 à 3 pages)',
      'Design sur mesure',
      'Optimisation mobile',
      'SEO de base',
      'Formulaire de contact',
      'Hébergement + maintenance inclus',
      'Résiliation à tout moment',
    ],
  },
  {
    name: 'Pro',
    prices: { sans: 129, '6mois': 99, '12mois': 79 },
    color: 'emerald',
    icon: 'ri-rocket-2-line',
    popular: true,
    badge: '⭐ POPULAIRE',
    description: 'Pour acquérir des clients rapidement',
    type: 'vitrine',
    features: [
      "Site complet (jusqu'à 5 pages)",
      'Design sur mesure',
      'Optimisation mobile',
      'SEO avancé',
      'Formulaire de contact',
      'Hébergement inclus',
      'Support prioritaire',
      'Résiliation à tout moment',
    ],
  },
  {
    name: 'Premium',
    prices: { sans: 199, '6mois': 159, '12mois': 129 },
    color: 'amber',
    icon: 'ri-vip-crown-2-line',
    popular: false,
    badge: '👑 PREMIUM',
    description: 'Croissance maximale, visibilité totale',
    type: 'vitrine',
    features: [
      'Site sur mesure (pages illimitées)',
      'Design avancé',
      'SEO + optimisation conversion',
      'Gestion fiche Google Business',
      'Formulaire de contact',
      'Hébergement inclus',
      'Modifications illimitées',
      'Résiliation à tout moment',
    ],
  },
];

export const ECOMMERCE_PLANS: Plan[] = [
  {
    name: 'Essentiel',
    prices: { sans: 149, '6mois': 119, '12mois': 99 },
    color: 'teal',
    icon: 'ri-store-line',
    popular: false,
    badge: null,
    description: 'Lancez votre boutique rapidement',
    type: 'ecommerce',
    features: [
      "Boutique en ligne (jusqu'à 10 produits)",
      'Design sur mesure',
      'Optimisation mobile',
      'SEO de base',
      'Paiement en ligne (Stripe)',
      'Gestion des commandes + stocks',
      'Hébergement inclus',
      'Résiliation à tout moment',
    ],
  },
  {
    name: 'Pro',
    prices: { sans: 249, '6mois': 199, '12mois': 169 },
    color: 'emerald',
    icon: 'ri-rocket-line',
    popular: true,
    badge: '⭐ POPULAIRE',
    description: 'Le meilleur rapport qualité / résultats',
    type: 'ecommerce',
    features: [
      "Boutique (jusqu'à 50 produits)",
      'Design sur mesure',
      'SEO avancé',
      "Tunnel d'achat optimisé",
      'Gestion des commandes + stocks',
      'Hébergement inclus',
      'Maintenance + modifications',
      'Résiliation à tout moment',
    ],
  },
  {
    name: 'Premium',
    prices: { sans: 399, '6mois': 329, '12mois': 279 },
    color: 'amber',
    icon: 'ri-vip-crown-line',
    popular: false,
    badge: '👑 PREMIUM',
    description: 'Boutique complète sans limites',
    type: 'ecommerce',
    features: [
      'Boutique complète (produits illimités)',
      'Design sur mesure avancé',
      'SEO avancé',
      'Tunnel de vente optimisé conversion',
      'Automatisations avancées',
      'Hébergement inclus',
      'Modifications illimitées',
      'Résiliation à tout moment',
    ],
  },
];

const ENGAGEMENT_LABELS: Record<Engagement, { short: string; long: string; savings: string | null }> = {
  sans: { short: 'Mensuel', long: 'Sans engagement', savings: null },
  '6mois': { short: '6 mois', long: 'Engagement 6 mois', savings: "Jusqu'à 23% d'économies" },
  '12mois': { short: '12 mois', long: 'Engagement 12 mois', savings: "Jusqu'à 35% d'économies" },
};

interface PlanSelectorProps {
  selectedPlan: Plan | null;
  onSelectPlan: (plan: Plan) => void;
  engagement?: Engagement;
  onEngagementChange?: (e: Engagement) => void;
}

export default function PlanSelector({ selectedPlan, onSelectPlan, engagement, onEngagementChange }: PlanSelectorProps) {
  const [activeType, setActiveType] = useState<PlanType>('vitrine');

  const plans = activeType === 'vitrine' ? VITRINE_PLANS : ECOMMERCE_PLANS;
  const engInfo = ENGAGEMENT_LABELS[engagement];

  return (
    <div>
      {/* Type selector */}
      <div className="flex bg-slate-100 rounded-2xl p-1.5 gap-1 mb-6">
        <button
          onClick={() => setActiveType('vitrine')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
            activeType === 'vitrine'
              ? 'bg-white text-slate-900'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <i className="ri-pages-line text-teal-500"></i>
          <span>Sites Vitrine</span>
        </button>
        <button
          onClick={() => setActiveType('ecommerce')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
            activeType === 'ecommerce'
              ? 'bg-white text-slate-900'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <i className="ri-shopping-cart-2-line text-emerald-500"></i>
          <span>Sites E-commerce</span>
        </button>
      </div>

      {/* Engagement selector */}
      <div className="mb-6">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Durée d&apos;engagement</p>
        <div className="flex gap-2">
          {(['sans', '6mois', '12mois'] as Engagement[]).map((e) => (
            <button
              key={e}
              onClick={() => onEngagementChange(e)}
              className={`flex-1 py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all duration-200 cursor-pointer whitespace-nowrap border-2 ${
                engagement === e
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-transparent'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'
              }`}
            >
              {ENGAGEMENT_LABELS[e].short}
            </button>
          ))}
        </div>
        {engInfo.savings && (
          <p className="mt-2 text-xs text-center text-amber-600 font-bold bg-amber-50 py-1.5 rounded-lg border border-amber-200">
            🎉 {engInfo.savings}
          </p>
        )}
      </div>

      {/* Plan cards */}
      <div className="flex flex-col gap-3">
        {plans.map((plan) => {
          const isSelected = selectedPlan?.name === plan.name && selectedPlan?.type === plan.type;
          const isEmerald = plan.color === 'emerald';
          const isAmber = plan.color === 'amber';
          const currentPrice = plan.prices[engagement];

          return (
            <div
              key={plan.name}
              onClick={() => onSelectPlan({ ...plan, prices: plan.prices })}
              className={`relative rounded-2xl border-2 p-4 cursor-pointer transition-all duration-200 overflow-hidden ${
                isSelected
                  ? isEmerald
                    ? 'border-emerald-400 bg-emerald-50/60'
                    : isAmber
                    ? 'border-amber-400 bg-amber-50/60'
                    : 'border-teal-400 bg-teal-50/60'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <div className="flex items-center gap-3">
                {/* Radio */}
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                    isSelected
                      ? isEmerald
                        ? 'border-emerald-500 bg-emerald-500'
                        : isAmber
                        ? 'border-amber-500 bg-amber-500'
                        : 'border-teal-500 bg-teal-500'
                      : 'border-slate-300 bg-white'
                  }`}
                >
                  {isSelected && (
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                  )}
                </div>

                {/* Icon */}
                <div
                  className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${
                    isEmerald ? 'bg-emerald-100' : isAmber ? 'bg-amber-100' : 'bg-teal-50'
                  }`}
                >
                  <i
                    className={`${plan.icon} text-lg ${
                      isEmerald ? 'text-emerald-600' : isAmber ? 'text-amber-600' : 'text-teal-600'
                    }`}
                  ></i>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-black text-slate-900">{plan.name}</p>
                      {plan.badge && (
                        <span
                          className={`text-[10px] font-black px-2 py-0.5 rounded-full whitespace-nowrap ${
                            plan.popular
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {plan.badge}
                        </span>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span
                        className={`text-xl font-black ${
                          isEmerald ? 'text-emerald-600' : isAmber ? 'text-amber-600' : 'text-teal-600'
                        }`}
                      >
                        {currentPrice}€
                      </span>
                      <span className="text-xs text-slate-400 font-medium">/mois</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 leading-tight mt-0.5 truncate">{plan.description}</p>
                </div>
              </div>

              {/* Features on selection */}
              {isSelected && (
                <div className="mt-4 pt-3 border-t border-slate-200 grid grid-cols-1 gap-1.5">
                  {plan.features.map((f, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div
                        className={`w-3.5 h-3.5 flex items-center justify-center rounded-full flex-shrink-0 ${
                          isEmerald ? 'bg-emerald-100' : isAmber ? 'bg-amber-100' : 'bg-teal-50'
                        }`}
                      >
                        <i
                          className={`ri-check-line text-[9px] ${
                            isEmerald ? 'text-emerald-600' : isAmber ? 'text-amber-600' : 'text-teal-600'
                          }`}
                        ></i>
                      </div>
                      <span className="text-xs text-slate-700">{f}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Engagement note */}
      <div className="mt-4 p-3 bg-slate-50 rounded-xl border border-slate-100">
        <div className="flex items-start gap-2">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-slate-400 text-sm"></i>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            <strong className="text-slate-700">Annulable à tout moment</strong> — Votre abonnement se renouvelle chaque mois. Vous pouvez résilier quand vous le souhaitez depuis votre espace client Stripe.
          </p>
        </div>
      </div>
    </div>
  );
}
